import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

// User information interface
interface UserInfo {
  strid: string
  name?: string
  email?: string
  workspace_strid?: string
  user_tier?: string
  position?: string
  company?: string
  terms_version?: string
  requires_terms_version?: string
  requires_terms_acceptance?: boolean
  language: string
  tenant_role?: 'ADMIN' | 'MEMBER'
}

interface SignUpData {
  email: string
  password: string
  name?: string
  strid?: string
  position?: string
  company?: string
  terms_version?: string
  requires_terms_version?: string
  user_tier?: string
  requires_terms_acceptance?: false
  isExistingUser?: boolean // Flag to distinguish existing users from new users
}

// User state interface with tenant information
interface UserState {
  mode:
    | 'login' // Login Page
    | 'signup' // Signup
    | 'personalInfo' // Personal Info
    | 'verifyEmail' // Verify Email
    | 'signupComplete' // Signup Complete
    | 'resetPassword' // Reset Password
    | 'checkInbox' // Check Inbox
    | 'createNewPassword' // Create New Password
    | 'passwordChanged' // Password Change Completed
    | 'completed' // Complete
  userInfo: UserInfo | null
  loading: boolean
  initialStateLoginPage: 'signUp' | 'signIn'
  currentWorkspace: string
  signUpData: SignUpData | null
  emailCodeSent: string | null
  renewPasswordRequest: {
    email: string
    code: string
    newPassword: string
  }
}

const initialState: UserState = {
  userInfo: null,
  loading: false,
  initialStateLoginPage: 'signIn',
  currentWorkspace: '',
  mode: 'login',
  signUpData: null,
  emailCodeSent: null,
  renewPasswordRequest: {
    email: '',
    code: '',
    newPassword: '',
  },
}

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setMode: (state, action: PayloadAction<UserState['mode']>) => {
      state.mode = action.payload
    },
    setUserInfo: (state, action: PayloadAction<UserInfo>) => {
      state.userInfo = action.payload // No more `never` errors!
      state.loading = false
    },
    updateUserInfo: (state, action: PayloadAction<Partial<UserInfo>>) => {
      if (state.userInfo) {
        state.userInfo = { ...state.userInfo, ...action.payload }
      }
    },
    clearUserInfo: (state) => {
      state.userInfo = null
    },
    setUserLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setInitialStateLoginPage: (
      state,
      action: PayloadAction<'signIn' | 'signUp'>
    ) => {
      state.initialStateLoginPage = action.payload
    },
    setCurrentWorkspace: (state, action: PayloadAction<string>) => {
      state.currentWorkspace = action.payload
    },
    setSignUpData: (state, action: PayloadAction<SignUpData>) => {
      state.signUpData = action.payload
    },
    setEmailCodeSent: (state, action: PayloadAction<string>) => {
      state.emailCodeSent = action.payload
    },
    setRenewPasswordRequest: (
      state,
      action: PayloadAction<{
        email: string
        code: string
        newPassword: string
      }>
    ) => {
      state.renewPasswordRequest = action.payload
    },
    clearSignUpData: (state) => {
      state.signUpData = null
    },
  },
})

export const {
  setUserInfo,
  clearUserInfo,
  setUserLoading,
  setInitialStateLoginPage,
  setCurrentWorkspace,
  setSignUpData,
  clearSignUpData,
  setMode,
  setEmailCodeSent,
  setRenewPasswordRequest,
  updateUserInfo,
} = userSlice.actions
export default userSlice.reducer
